import { useAtomValue, useSetAtom } from "jotai";
import { Suspense, lazy, useCallback, useEffect, useId, useMemo, useRef, useState, type FC } from "react";
import type { ImperativePanelGroupHandle, ImperativePanelHandle } from "react-resizable-panels";
import { getPanelElement, getPanelGroupElement } from "react-resizable-panels";
import { Outlet, useLocation, useParams } from "react-router-dom";

import { useRegisterEnvironmentCommands } from "@/actions/use-register-environment-commands";
import { detailsPanelToggleAtom, fileOpenRequestAtom, superOnaContentTabAtom } from "@/atoms/ui-state";
import { DiffsPoolProvider } from "@/components/ai/DiffsPoolProvider";
import { useReviewComments, type ReviewCommentsHandle } from "@/components/ai/review/use-review-comments";
import type { EnvironmentViewMode } from "@/components/ai/ViewModeToggle";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/flexkit/ResizablePanel";
import { cn } from "@/components/podkit/lib/cn";
import { MainContent } from "@/components/SidebarLayout";
import { VSCodeIpcProvider } from "@/contexts/VSCodeIpcContext";
import type { AnnotationSyncHandle } from "@/hooks/use-agent-annotation-sync";
import { useDynamicTitle } from "@/hooks/use-dynamic-title";
import { useEnsureVscodeServer } from "@/hooks/use-ensure-vscode-server";
import { useEnvironmentConversation, getAgentExecution } from "@/hooks/use-environment-conversation";
import type { EnvironmentDetailsOutletContext } from "@/hooks/use-environment-details-outlet-context";
import {
    mergeEnvironmentTabState,
    readEnvironmentTabState,
    useEnvironmentTabState,
    type PersistedTabState,
} from "@/hooks/use-environment-tab-state";
import { useFlagOnaAIEnabled, useFlagOnaEnvironmentOnboardingEnabled } from "@/hooks/use-flag";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { useMountEffect } from "@/hooks/use-mount-effect";
import { useStreamEvents } from "@/hooks/use-stream-events";
import { useIsWelcomeEligible } from "@/hooks/use-welcome-eligible";
import { useAgentExecutionsForEnvironment, type PlainAgentExecution } from "@/queries/agent-queries";
import { asNavState } from "@/routes/environments/details/environment-nav-state";
import { type CommentCountByFile } from "@/routes/environments/details/file-tree-utils";
import type { FileDiffReviewComments } from "@/routes/environments/details/SuperOnaFileDiffPanel";

const LazySuperOnaEnvironmentContent = lazy(() =>
    import("@/routes/environments/details/SuperOnaEnvironmentContent").then((m) => ({
        default: m.SuperOnaEnvironmentContent,
    })),
);

const LazySuperOnaEnvironmentDetailsPanel = lazy(() =>
    import("@/routes/environments/details/SuperOnaEnvironmentDetailsPanel").then((m) => ({
        default: m.SuperOnaEnvironmentDetailsPanel,
    })),
);

export const EnvironmentDetailsLayout: FC = () => {
    useDynamicTitle("Environment");
    useEnsureVscodeServer();

    const { environmentId = "" } = useParams();

    // Reset cross-component signaling atoms when the environment changes.
    // Without this, a stale contentTab seq would override the tab restored
    // from sessionStorage, and a stale fileOpenRequest would show a file
    // tab from the previous environment.
    const setSuperOnaContentTab = useSetAtom(superOnaContentTabAtom);
    const setFileOpenRequest = useSetAtom(fileOpenRequestAtom);
    useEffect(() => {
        setSuperOnaContentTab({ tab: "conversation", seq: 0 });
        setFileOpenRequest({ filePath: "", lineNumber: null, seq: 0 });
    }, [environmentId, setSuperOnaContentTab, setFileOpenRequest]);

    const location = useLocation();
    const navState = asNavState(location.state);
    const [preferredAgentExecutionId, setPreferredAgentExecutionId] = useState<string | undefined>(
        () =>
            navState?.preferredAgentExecutionId ??
            readEnvironmentTabState(environmentId)?.selectedExecutionId ??
            undefined,
    );
    const scope = useMemo(() => ({ case: "environmentId" as const, value: environmentId }), [environmentId]);
    useStreamEvents(scope);
    const { environmentConversation } = useEnvironmentConversation(environmentId, preferredAgentExecutionId);
    const { value: isOnaEnvironmentOnboardingEnabled } = useFlagOnaEnvironmentOnboardingEnabled();
    const { value: isOnaEnabled } = useFlagOnaAIEnabled();

    useEffect(() => {
        if (!navState?.preferredAgentExecutionId) {
            return;
        }

        setPreferredAgentExecutionId(navState.preferredAgentExecutionId);

        if (!navState.switchToDetails && !navState.switchToVscode) {
            window.history.replaceState({}, "", location.pathname);
        }
    }, [navState?.preferredAgentExecutionId, navState?.switchToDetails, navState?.switchToVscode, location.pathname]);

    const isFeatureFlagEnabled = isOnaEnabled || isOnaEnvironmentOnboardingEnabled;

    const handleSelectExecution = useCallback(
        (executionId: string) => {
            setPreferredAgentExecutionId(executionId);
            mergeEnvironmentTabState(environmentId, { selectedExecutionId: executionId });
        },
        [environmentId],
    );

    // Always show the conversation layout. AI features are gated by isFeatureFlagEnabled.
    const outletContext: EnvironmentDetailsOutletContext = useMemo(() => ({ showingAgentConversation: true }), []);

    return (
        <VSCodeIpcProvider>
            <EnvironmentCommandPaletteRegistration environmentId={environmentId} />
            <EnvironmentDetailsWithConversation
                key={environmentId}
                environmentConversation={isFeatureFlagEnabled ? environmentConversation : null}
                isAIEnabled={isFeatureFlagEnabled}
                environmentId={environmentId}
                preferConversationOnMobile={!navState?.switchToDetails && !navState?.switchToVscode}
                onSelectExecution={handleSelectExecution}
            />
            {/* In the VS Code webview there are no child routes — SuperOnaEnvironmentContent
                renders everything inline. On the dashboard the index route is also handled
                inline, so the Outlet is only needed for child routes (logs, services, tasks). */}
            {!window.isWebview && <Outlet context={outletContext} />}
        </VSCodeIpcProvider>
    );
};

const EnvironmentCommandPaletteRegistration: FC<{ environmentId: string }> = ({ environmentId }) => {
    const modals = useRegisterEnvironmentCommands(environmentId);
    return <>{modals}</>;
};

type EnvironmentAgentExecutionConversationPanelProps = {
    environmentConversation: ReturnType<typeof useEnvironmentConversation>["environmentConversation"];
    /** Whether AI/agents are enabled for this org (distinct from whether an execution exists yet). */
    isAIEnabled: boolean;
    environmentId: string;
    isDetailsPanelOpen: boolean;
    onToggleDetailsPanel: () => void;
    onBuildButtonAppear?: (specFilePath: string | null) => void;
    selectedFilePath?: string | null;
    selectedFileLineNumber?: number | null;
    onFileSelect?: (filePath: string | null) => void;
    /** Whether to auto-start the environment when the file tab is shown. False when the file tab was restored from persisted state. */
    fileAutoStart?: boolean;
    reviewComments?: FileDiffReviewComments;
    annotationSyncHandle?: AnnotationSyncHandle;
    allExecutions?: PlainAgentExecution[];
    onSelectExecution?: (executionId: string) => void;
    initialTabState?: PersistedTabState | null;
    persistTabState?: (partial: Partial<PersistedTabState>) => void;
};

const EnvironmentAgentExecutionConversationPanel: FC<EnvironmentAgentExecutionConversationPanelProps> = ({
    environmentConversation,
    isAIEnabled,
    environmentId,
    isDetailsPanelOpen,
    onToggleDetailsPanel,
    onBuildButtonAppear,
    selectedFilePath,
    selectedFileLineNumber,
    onFileSelect,
    fileAutoStart,
    reviewComments,
    annotationSyncHandle,
    allExecutions,
    onSelectExecution,
    initialTabState,
    persistTabState,
}) => {
    const agentExecution = getAgentExecution(environmentConversation);

    return (
        <Suspense fallback={null}>
            <LazySuperOnaEnvironmentContent
                agentExecution={agentExecution}
                isAIEnabled={isAIEnabled}
                isDetailsPanelOpen={isDetailsPanelOpen}
                onToggleDetailsPanel={onToggleDetailsPanel}
                environmentId={environmentId}
                onBuildButtonAppear={onBuildButtonAppear}
                selectedFilePath={selectedFilePath}
                selectedFileLineNumber={selectedFileLineNumber}
                onFileSelect={onFileSelect}
                fileAutoStart={fileAutoStart}
                reviewComments={reviewComments}
                annotationSyncHandle={annotationSyncHandle}
                allExecutions={allExecutions}
                onSelectExecution={onSelectExecution}
                initialTabState={initialTabState}
                persistTabState={persistTabState}
            />
        </Suspense>
    );
};

/**
 * Shared state for both EnvironmentDetailsPanel and SuperOnaEnvironmentDetailsPanel:
 * environment/runner data, view mode, and parent-driven view mode / notification effects.
 */
const EnvironmentDetailsWithConversation: FC<{
    environmentConversation: ReturnType<typeof useEnvironmentConversation>["environmentConversation"];
    /** Whether AI/agents are enabled for this org (distinct from whether an execution exists yet). */
    isAIEnabled: boolean;
    environmentId: string;
    preferConversationOnMobile?: boolean;
    onSelectExecution?: (executionId: string) => void;
}> = ({
    environmentConversation,
    isAIEnabled,
    environmentId,
    preferConversationOnMobile = false,
    onSelectExecution,
}) => {
    // Simple rule: track if code view is expanded per environment
    // Expanded code view = conversation hidden, everything else = conversation visible
    const getCodeViewExpanded = useCallback(() => {
        const key = `environment-code-expanded:${environmentId}`;
        return localStorage.getItem(key) === "true";
    }, [environmentId]);

    const setCodeViewExpanded = useCallback(
        (expanded: boolean) => {
            const key = `environment-code-expanded:${environmentId}`;
            localStorage.setItem(key, String(expanded));
        },
        [environmentId],
    );

    // Persist/restore tab state across environment switches and page reloads.
    const { initialState: initialTabState, persist: persistTabState } = useEnvironmentTabState(environmentId);

    const [isConversationPanelCollapsed, setIsConversationPanelCollapsed] = useState(false);
    const showConversationPanel = !isConversationPanelCollapsed;
    const [currentViewMode, setCurrentViewMode] = useState<EnvironmentViewMode>(initialTabState?.viewMode ?? "details");

    // All agent executions for this environment, including subagents (for execution tabs)
    const { data: allExecutionsRaw } = useAgentExecutionsForEnvironment(environmentId);
    // Sort oldest-first so the main conversation appears leftmost in tabs
    const allExecutions = useMemo(
        () =>
            [...(allExecutionsRaw ?? [])].sort((a, b) =>
                Number((a.metadata?.createdAt?.seconds ?? BigInt(0)) - (b.metadata?.createdAt?.seconds ?? BigInt(0))),
            ),
        [allExecutionsRaw],
    );

    // File selected from the right-panel tree — opens a diff tab in the left panel.
    // When the file path is restored from persisted state (sessionStorage), the
    // user didn't explicitly click a file, so we should NOT auto-start a stopped
    // environment. fileAutoStart tracks this: false when restored, true when the
    // user explicitly selects a file.
    const [selectedFilePath, setSelectedFilePath] = useState<string | null>(initialTabState?.selectedFilePath ?? null);
    const [selectedFileLineNumber, setSelectedFileLineNumber] = useState<number | null>(null);
    const [fileAutoStart, setFileAutoStart] = useState(
        // If the file was restored from persisted state, don't auto-start.
        initialTabState?.selectedFilePath == null,
    );
    const handleFileSelect = useCallback((filePath: string | null, lineNumber?: number | null) => {
        setSelectedFilePath(filePath);
        setSelectedFileLineNumber(filePath ? (lineNumber ?? null) : null);
        setFileAutoStart(true);
    }, []);

    // Persist selectedFilePath and viewMode changes to sessionStorage.
    useEffect(() => {
        persistTabState({ selectedFilePath, viewMode: currentViewMode });
    }, [selectedFilePath, currentViewMode, persistTabState]);

    // Open a file when requested from the conversation (e.g. clicking the
    // code icon on an inline diff header or a code annotation).
    // The useOpenFile hook already converts absolute paths to repo-relative.
    const fileOpenRequest = useAtomValue(fileOpenRequestAtom);
    useEffect(() => {
        if (fileOpenRequest.seq === 0) return;
        handleFileSelect(fileOpenRequest.filePath, fileOpenRequest.lineNumber);
    }, [fileOpenRequest, handleFileSelect]);

    // Single source of truth for review comments — shared between the file diff
    // tab (left panel), the file tree comment counters (right panel), and the
    // submit review button in the right panel header.
    const {
        comments: reviewComments,
        commentsByFile,
        clearAllComments,
        addComment,
        updateComment,
        removeComment,
        setCommentThumbsUp,
        setCommentThumbsDown,
        isAnnotationDismissed,
    } = useReviewComments(environmentId);

    // Handle passed to SuperOnaEnvironmentContent so it can call
    // useAgentAnnotationSync with the stream messages it already owns.
    const annotationSyncHandle: AnnotationSyncHandle = useMemo(
        () => ({ comments: reviewComments, addComment, isAnnotationDismissed }),
        [reviewComments, addComment, isAnnotationDismissed],
    );

    // Handle passed to the file diff tab so it shares the same comment state.
    // Not gated on the review-comments flag here — SuperOnaFileDiffPanel decides whether
    // to enable the comment UI based on whether this handle is provided, and the
    // flag is already checked by the parent when deciding whether to render the
    // submit-review button.
    const fileDiffReviewComments: FileDiffReviewComments = useMemo(
        () => ({
            commentsByFile,
            addComment,
            updateComment,
            removeComment,
            clearAllComments,
            setCommentThumbsUp,
            setCommentThumbsDown,
        }),
        [
            commentsByFile,
            addComment,
            updateComment,
            removeComment,
            clearAllComments,
            setCommentThumbsUp,
            setCommentThumbsDown,
        ],
    );
    const commentCountByFile: CommentCountByFile = useMemo(() => {
        const counts: Record<string, number> = {};
        for (const [filePath, comments] of Object.entries(commentsByFile)) {
            if (comments && comments.length > 0) {
                counts[filePath] = comments.length;
            }
        }
        return counts;
    }, [commentsByFile]);

    // Handle passed to EnvironmentDetailsPanel → EnvironmentChangesView →
    // CodeChangesPanel so the non-super_ona changes view shares the same
    // comment state instead of creating a second useReviewComments instance.
    const codeChangesReviewComments: ReviewCommentsHandle = useMemo(
        () => ({
            comments: reviewComments,
            commentsByFile,
            addComment,
            updateComment,
            removeComment,
            clearAllComments,
            setCommentThumbsUp,
            setCommentThumbsDown,
        }),
        [
            reviewComments,
            commentsByFile,
            addComment,
            updateComment,
            removeComment,
            clearAllComments,
            setCommentThumbsUp,
            setCommentThumbsDown,
        ],
    );

    const [fixedConversationPanelSize, setFixedConversationPanelSize] = useState(0);
    const conversationPanelRef = useRef<ImperativePanelHandle>(null);
    const panelGroupId = useId();
    const panelGroupRef = useRef<ImperativePanelGroupHandle>(null);
    const panelRef = useRef<ImperativePanelHandle>(null);
    const isWelcomeEligible = useIsWelcomeEligible();
    const { isMobileViewport: isMobileViewport } = useIsMobileViewport();
    // In the VS Code webview, always use the desktop resizable layout to avoid
    // component remounts (and state resets) when the panel is resized across
    // the mobile breakpoint.
    const isMobile = window.isWebview ? false : isMobileViewport;
    const shouldStartOnConversationPanel = isMobile && preferConversationOnMobile;
    const [isDetailsPanelCollapsed, setIsDetailsPanelCollapsed] = useState(() => shouldStartOnConversationPanel);
    const hasAutoCollapsedForWelcome = useRef(false);

    // Collapse the details panel for welcome-eligible users so the
    // conversation panel gets full width on first load.
    useEffect(() => {
        if (isWelcomeEligible && !hasAutoCollapsedForWelcome.current && panelRef.current) {
            hasAutoCollapsedForWelcome.current = true;
            requestAnimationFrame(() => {
                panelRef.current?.collapse();
            });
        }
    }, [isWelcomeEligible]);
    const updatePanelSizeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const conversationPanelSize = showConversationPanel
        ? { defaultSize: 70, minSize: 30 }
        : { defaultSize: 0, minSize: 0 };

    // The library doesn't support animating the collapse/expand transition of panels [1]
    // You achieve it using CSS transitions, however, that breaks drag resizing behavior/performance
    // so the workaround is to only add those transitions when not dragging.
    //
    // Additionally, we suppress transitions on mount so that restoring a saved
    // layout (via autoSaveId) doesn't play an expand animation when switching
    // between sessions.
    //
    // [1] https://github.com/bvaughn/react-resizable-panels/issues/310#issuecomment-1974779047
    const [isDragging, setIsDragging] = useState(false);
    const [hasMounted, setHasMounted] = useState(false);
    useMountEffect(() => {
        setHasMounted(true);
    });

    useEffect(
        () => () => {
            if (updatePanelSizeTimeout.current) {
                clearTimeout(updatePanelSizeTimeout.current);
            }
        },
        [],
    );

    // Ensure conversation is always visible when in details or changes mode
    useEffect(() => {
        if ((currentViewMode === "details" || currentViewMode === "changes") && isConversationPanelCollapsed) {
            if (conversationPanelRef.current?.isCollapsed()) {
                conversationPanelRef.current.expand();
            }
            setIsConversationPanelCollapsed(false);
        }
    }, [currentViewMode, isConversationPanelCollapsed]);

    const handleToggleDetailsPanel = useCallback(() => {
        if (!panelRef.current) {
            return;
        }

        setFixedConversationPanelSize(0);

        if (panelRef.current.isCollapsed()) {
            panelRef.current.expand();
        } else {
            panelRef.current.collapse();
        }
    }, [panelRef]);

    // Expose the details panel toggle to the DesktopTitlebar and SuperOnaMobileHeader via atom
    const setDetailsPanelToggle = useSetAtom(detailsPanelToggleAtom);
    const mobileAwareToggle = useCallback(() => {
        if (isMobile) {
            setIsDetailsPanelCollapsed((prev) => !prev);
        } else {
            handleToggleDetailsPanel();
        }
    }, [isMobile, handleToggleDetailsPanel]);
    useEffect(() => {
        setDetailsPanelToggle({ toggle: mobileAwareToggle, isOpen: !isDetailsPanelCollapsed });
        return () => setDetailsPanelToggle(null);
    }, [mobileAwareToggle, isDetailsPanelCollapsed, setDetailsPanelToggle]);

    const onViewModeChange = useCallback(
        (mode: EnvironmentViewMode) => {
            setCurrentViewMode(mode);
            switch (mode) {
                case "details":
                case "changes": {
                    // Switching to details/changes view = minimizing code view = show conversation
                    if (conversationPanelRef.current?.isCollapsed()) {
                        conversationPanelRef.current.expand();
                        setIsConversationPanelCollapsed(false);
                    }
                    setCodeViewExpanded(false);
                    break;
                }
                case "vscode": {
                    // Switching to code view - restore per-environment expanded state
                    const isExpanded = getCodeViewExpanded();
                    const isCurrentlyCollapsed = conversationPanelRef.current?.isCollapsed() ?? false;

                    if (isExpanded && !isCurrentlyCollapsed) {
                        // Should be expanded (conversation hidden)
                        conversationPanelRef.current?.collapse();
                        setIsConversationPanelCollapsed(true);
                    } else if (!isExpanded && isCurrentlyCollapsed) {
                        // Should not be expanded (conversation visible)
                        conversationPanelRef.current?.expand();
                        setIsConversationPanelCollapsed(false);
                    }
                    break;
                }
            }

            const id = conversationPanelRef.current?.getId();
            const conversationPanelElement = document.getElementById(id ?? "");
            if (mode === "vscode" && conversationPanelElement) {
                setFixedConversationPanelSize(conversationPanelElement.clientWidth);
                // wait for sidebar to finish animating out, then calculate update the panel proportion imperatively
                updatePanelSizeTimeout.current = setTimeout(() => {
                    const groupElement = getPanelGroupElement(panelGroupId);
                    const id = conversationPanelRef.current?.getId();
                    const conversationPanelElement = getPanelElement(id ?? "");
                    if (groupElement && conversationPanelElement && panelGroupRef.current) {
                        const proportionalSize =
                            (conversationPanelElement.clientWidth / groupElement.clientWidth) * 100;
                        panelGroupRef.current.setLayout([proportionalSize, 100 - proportionalSize]);
                    }
                }, 250);
            }
        },
        [getCodeViewExpanded, setCodeViewExpanded, panelGroupId],
    );

    const handleBuildButtonAppear = useCallback(
        (_specFilePath: string | null) => {
            if (isMobile) return;

            if (panelRef.current?.isCollapsed()) {
                panelRef.current.expand();
            }

            onViewModeChange("changes");
        },
        [isMobile, onViewModeChange, panelRef],
    );

    const onDragging = (isDragging: boolean) => {
        setIsDragging(isDragging);
        if (fixedConversationPanelSize) {
            setFixedConversationPanelSize(0);
        }
    };

    useEffect(() => {
        if (shouldStartOnConversationPanel) {
            setIsDetailsPanelCollapsed(true);
        }
    }, [shouldStartOnConversationPanel]);

    // On mobile, switch to the conversation panel whenever a producer bumps
    // superOnaContentTabAtom (file selected, logs opened, agent message sent).
    // Reacts to seq *changes* so it doesn't keep forcing the panel closed.
    const contentTabSeq = useAtomValue(superOnaContentTabAtom).seq;
    const prevContentTabSeq = useRef(contentTabSeq);
    useEffect(() => {
        if (isMobile && contentTabSeq !== prevContentTabSeq.current) {
            prevContentTabSeq.current = contentTabSeq;
            setIsDetailsPanelCollapsed(true);
        }
    }, [isMobile, contentTabSeq]);

    const conversationPanel = (
        <EnvironmentAgentExecutionConversationPanel
            environmentConversation={environmentConversation}
            isAIEnabled={isAIEnabled}
            environmentId={environmentId}
            isDetailsPanelOpen={isMobile ? false : !isDetailsPanelCollapsed}
            onToggleDetailsPanel={isMobile ? () => setIsDetailsPanelCollapsed(false) : handleToggleDetailsPanel}
            onBuildButtonAppear={handleBuildButtonAppear}
            selectedFilePath={selectedFilePath}
            selectedFileLineNumber={selectedFileLineNumber}
            onFileSelect={handleFileSelect}
            fileAutoStart={fileAutoStart}
            reviewComments={fileDiffReviewComments}
            annotationSyncHandle={annotationSyncHandle}
            allExecutions={allExecutions}
            onSelectExecution={onSelectExecution}
            initialTabState={initialTabState}
            persistTabState={persistTabState}
        />
    );

    const detailsPanel = (
        <Suspense>
            <LazySuperOnaEnvironmentDetailsPanel
                environmentId={environmentId}
                isAIEnabled={isAIEnabled}
                selectedFilePath={selectedFilePath}
                onFileSelect={handleFileSelect}
                commentCountByFile={commentCountByFile}
                reviewCommentsHandle={codeChangesReviewComments}
                useInlineDiffs={isMobile}
                environmentConversation={environmentConversation}
                initialTabState={initialTabState}
                persistTabState={persistTabState}
            />
        </Suspense>
    );

    if (isMobile) {
        return (
            <DiffsPoolProvider>
                <MainContent noPadding>{isDetailsPanelCollapsed ? conversationPanel : detailsPanel}</MainContent>
            </DiffsPoolProvider>
        );
    }

    return (
        <DiffsPoolProvider>
            <MainContent noPadding>
                <ResizablePanelGroup
                    id={panelGroupId}
                    ref={panelGroupRef}
                    direction="horizontal"
                    className="flex"
                    autoSaveId="environment-details-layout"
                >
                    <ResizablePanel
                        ref={conversationPanelRef}
                        defaultSize={conversationPanelSize.defaultSize}
                        minSize={conversationPanelSize.minSize}
                        collapsedSize={0}
                        collapsible={currentViewMode === "vscode"}
                        order={1}
                        className={cn({
                            // See definition of isDragging for why we're doing this selectively.
                            // Also suppress on mount so restoring a saved layout doesn't animate.
                            "transition-[flex-basis,flex-grow,flex-shrink] duration-100 ease-in-out":
                                hasMounted && !isDragging,
                        })}
                        style={
                            fixedConversationPanelSize
                                ? {
                                      maxWidth: `${fixedConversationPanelSize}px`,
                                  }
                                : {}
                        }
                        onCollapse={() => setIsConversationPanelCollapsed(true)}
                        onExpand={() => setIsConversationPanelCollapsed(false)}
                        id="conversation-panel"
                    >
                        {conversationPanel}
                    </ResizablePanel>
                    <ResizableHandle
                        disabled={isConversationPanelCollapsed || isDetailsPanelCollapsed}
                        onDragging={onDragging}
                        className={cn(
                            "border-l border-border-base",
                            (isConversationPanelCollapsed || isDetailsPanelCollapsed) && "border-l-0",
                        )}
                    />
                    <ResizablePanel
                        defaultSize={100 - conversationPanelSize.defaultSize}
                        minSize={30}
                        collapsedSize={0}
                        ref={panelRef}
                        collapsible
                        className={cn({
                            // See definition of isDragging for why we're doing this selectively.
                            // Also suppress on mount so restoring a saved layout doesn't animate.
                            "transition-[flex-basis,flex-grow,flex-shrink] duration-100 ease-in-out":
                                hasMounted && !isDragging,
                        })}
                        onCollapse={() => setIsDetailsPanelCollapsed(true)}
                        onExpand={() => setIsDetailsPanelCollapsed(false)}
                        order={2}
                        id="environment-details"
                    >
                        {detailsPanel}
                    </ResizablePanel>
                </ResizablePanelGroup>
            </MainContent>
        </DiffsPoolProvider>
    );
};
